<template>
  <fts-design-modal
    class="fts-information-design-modal"
    ref="ftsDesignModal"
  >
    <!-- title -->
    <template #title>
      <!-- system -->
      <template v-if="isSystem">
        <div class="design-modal-header-std__title">
          <i
            :class="informationIcon(formData.type)"
            class="design-modal-header-icon information-icon system"
          />
          <div class="design-modal-header-name">
            {{ formData.displayName }}
          </div>
        </div>
      </template>
      <!-- information -->
      <template v-else>
        <div class="design-modal-header-std__title with-hint">
          <i
            :class="informationIcon(formData.type)"
            class="design-modal-header-icon information-icon"
          />
          <input
            type="text"
            placeholder="Please enter a name"
            class="fts-form-control design-modal-header-input"
            :class="{
              error: $v.formData.displayName && $v.formData.displayName.$error,
            }"
            v-model="formData.displayName"
            spellcheck="false"
            @focus="designNameHasFocus = true"
            @blur="designNameHasFocus = false"
            @input="dispatchAutofill"
            @keydown.enter="doneContinue"
            ref="designerName"
          />
          <!-- parent -->
          <div class="information-parent" v-if="findParent">
            <div class="information-parent-seperator">in</div>
            <i
              class="information-parent-icon"
              :class="informationIcon(findParent.type)"
            />
            <div class="information-parent-name">
              {{ findParent.displayName }}
            </div>
          </div>
          <div class="design-modal-header-hint" v-if="headerHintHelper">
            press enter to continue
          </div>
        </div>
        <div class="design-modal-form-error" v-if="error('displayName')">
          {{ error("displayName") }}
        </div>
      </template>
    </template>
    <!-- description -->
    <template #description>
      <span class="design-modal-header-tag">Information</span>
      <span class="design-modal-header-description">
        specify the name and type of a field or input
      </span>
    </template>
    <!-- body -->
    <template #body>
      <!-- spinner -->
      <div class="design-modal-spinner" v-if="loading">
        <fts-spinner show />
      </div>
      <!-- system -->
      <template v-else-if="isSystem">
        <!-- description -->
        <div
          class="design-modal-form-group design-modal-body-description"
          v-if="formData.description"
        >
          {{ formData.description }}
        </div>
        <!-- document Id -->
        <template v-if="formData.id === 'documentId'">
          <!-- format -->
          <fts-form-element
            :formData="formData"
            :item="{
              fieldName: 'documentIdFormat',
              displayName: 'Format',
              type: 0,
            }"
            comfort-design
          >
            <fts-template-editor
              slot="formElement"
              slot-scope="{ formData, item }"
              :information="information"
              v-model="formData[item.fieldName]"
            />
          </fts-form-element>
          <!-- serial number -->
          <div class="serial-number" v-if="!serialNumberError">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <div class="fts-form-label">
                  Current Serial Number:
                  <span class="fts-text-primary fts-font-bold">{{serialNumber}}</span>
                </div>
                <div class="fts-form-helper">
                  Set 0 to reset serial number. New serial number must be
                  greater or equal to the maximum serial number of all entries
                  in the module to prevent duplicate ID.
                </div>
              </div>
              <div class="design-modal-label-group-r">
                <fts-btn
                  outline
                  sm
                  text-transform-init
                  @click="
                    serialNumberExpand = !serialNumberExpand
                  "
                >
                  {{ serialNumberExpand ? "Cancel" : "Update" }}
                </fts-btn>
              </div>
            </div>
            <template v-if="serialNumberExpand">
              <fts-form-element
                :formData="serialNumberPayload"
                :item="{
                  fieldName: 'serialNumber',
                  displayName: 'New Serial Number',
                  type: 3,
                  decimalPlaces: 0,
                }"
                comfort-design />
              <div class="serial-number-action">
                <fts-btn
                  outline
                  sm
                  color="primary"
                  text-transform-init
                  @click="saveSerial">
                  Update Serial Number
                </fts-btn>
              </div>
            </template>
          </div>
        </template>
        <!-- format -->
        <fts-form-element
          :formData="formData"
          :item="{
            fieldName: 'title',
            displayName: 'Format',
            type: 0,
          }"
          comfort-design
          v-if="formData.id === 'title'"
        >
          <fts-template-editor
            slot="formElement"
            slot-scope="{ formData, item }"
            :information="information"
            v-model="formData[item.fieldName]"
          />
        </fts-form-element>
      </template>
      <!-- information -->
      <template v-else>
        <!-- description -->
        <div class="fts-form-group">
          <fts-contenteditable
            v-model="formData.description"
            text-wrap
            placeholder="enter description (optional)"
          />
        </div>
        <!-- type -->
        <fts-form-element
          :form-data="formData"
          :item="{
            displayName: 'Information Type',
            fieldName: 'type',
            icon: 'fts-icon-menu-caret-down',
            required: true,
          }"
          :validator="$v.formData"
          comfort-design
        >
          <fts-select
            slot="formElement"
            slot-scope="{ formData, item, onInput, onBlur }"
            :form-data="formData"
            :item="item"
            label="type"
            comfort-design
            :options="informationTypes.filter(x => !x.internal)"
            @input="onInput"
            @search:blur="onBlur"
          >
            <div
              slot="selected-option-container"
              slot-scope="{ option }"
              class="information-type"
            >
              <i :class="option.icon" class="information-type-icon" />
              <div class="information-type-name">{{ option.type }}</div>
            </div>
            <div slot="option" slot-scope="{ option }" class="information-type">
              <i :class="option.icon" class="information-type-icon" />
              <div class="information-type-name">{{ option.type }}</div>
              <div class="information-hint">
                &nbsp;&nbsp;{{ getHint(option.id) }}
              </div>
            </div>
          </fts-select>
        </fts-form-element>
        <!-- type hint -->
        <div class="design-modal-form-group information-type-hint-helper">
          {{ getHint(formData.type) }}
        </div>
        <!-- connection type -->
        <template v-if="isConnectionType">
          <!-- connection -->
          <fts-form-element
            :form-data="formData"
            :item="{
              displayName: 'Module',
              fieldName: 'connectionId',
              icon: 'fts-icon-menu-caret-down',
              required: true,
            }"
            :validator="$v.formData"
            comfort-design
          >
            <fts-select
              slot="formElement"
              slot-scope="{ formData, item, onInput, onBlur }"
              :form-data="formData"
              :item="item"
              :options="moduleOptions"
              comfort-design
              @input="onInput"
              @search:focus="preloadModules"
              @search:blur="onBlur"
              @optionSelected="updateConnection"
              @clearSelection="updateConnection"
            >
              <fts-label-group
                slot="selected-option-container"
                slot-scope="{ option }"
                :item-id="option.id"
                :list="workspaceModules"
                icon-class="design-modal-module-option-emoji"
                emoji
              />
              <fts-label-group
                slot="option"
                slot-scope="{ option }"
                :item-id="option.id"
                :list="workspaceModules"
                icon-class="design-modal-module-option-emoji"
                emoji
              />
            </fts-select>
          </fts-form-element>
          <!-- connection field -->
          <fts-form-element
            :form-data="formData"
            :item="{
              displayName: 'Display Information',
              fieldName: 'connectionField',
              icon: 'fts-icon-menu-caret-down',
              required: true,
            }"
            :validator="$v.formData"
            comfort-design
            v-if="formData.connectionId"
          >
            <fts-select
              slot="formElement"
              slot-scope="{ formData, item, onInput, onBlur }"
              :form-data="formData"
              :item="item"
              reduceKey="displayName"
              label="displayName"
              :options="moduleInformation"
              :key="formData.connectionField"
              comfort-design
              @input="onInput"
              @search:blur="onBlur"
              @optionSelected="updateConnectionField"
              @clearSelection="updateConnectionField"
            >
              <div
                slot="selected-option-container"
                slot-scope="{ option }"
                class="information-type"
                :class="{ system: !option.id }"
              >
                <i
                  :class="informationIcon(option.type)"
                  class="information-type-icon"
                />
                <div class="information-type-name">
                  {{ option.displayName }}
                </div>
              </div>
              <div
                slot="option"
                slot-scope="{ option }"
                class="information-type"
                :class="{ system: !option.id }"
              >
                <i
                  :class="informationIcon(option.type)"
                  class="information-type-icon"
                />
                <div class="information-type-name">
                  {{ option.displayName }}
                </div>
              </div>
            </fts-select>
          </fts-form-element>
        </template>
        <!-- select:switch -->
        <template v-if="formData.type == 27 && beta">
          <div class="information-select-switch">
            <div
              class="select-switch"
              :class="{'select-switch--active': !formData.reference}"
              @click="toggleSelectSwitch(false)"
            >
              User-Defined
            </div>
            <div
              class="select-switch"
              :class="{'select-switch--active': formData.reference}"
              @click="toggleSelectSwitch(true)"
            >
              Reference&nbsp;
              <div>
                <fts-release-badge class="fts-align-top" />
              </div>
            </div>
          </div>
        </template>
        <!-- select:reference -->
        <div
          class="design-modal-form-group design-modal-card design-modal-card-mt"
          v-if="formData.type === 27 && formData.reference && beta"
        >
          <div class="fts-row gutters">
            <div class="fts-col">
              <fts-release-badge class="fts-float-right" />
            </div>
            <!-- reference:module -->
            <fts-form-element
              :form-data="formData"
              :item="{
                displayName: 'Reference Module',
                fieldName: 'referenceModuleFieldId',
                icon: 'fts-icon-menu-caret-down',
                required: true,
              }"
              :validator="$v.formData"
              comfort-design
              class="fts-col"
              key="referenceModuleFieldId"
            >
              <template #formElement="{formData, item, onInput, onBlur}">
                <fts-select
                :form-data="formData"
                :item="item"
                label="displayName"
                :options="referenceInformation"
                comfort-design
                @input="onInput"
                @search:blur="onBlur"
                @optionSelected="updateReferenceModule"
                @clearSelection="updateReferenceModule"
              >
                <div
                  slot="selected-option-container"
                  slot-scope="{ option }"
                  class="information-type"
                >
                  <i :class="informationIcon(option.type)" class="information-type-icon" />
                  <div class="information-type-name">{{ option.displayName }}</div>
                  <!-- module -->
                  <fts-label-group
                    :item="getModule(option.connectionId)"
                    emoji
                    :emoji-size="12"
                    class="information-module-tag"
                    v-if="option.connectionId && getModule(option.connectionId)"
                  />
                  <!-- parent -->
                  <div class="information-parent-tag" v-if="option.parentDisplayName">
                    <div class="information-parent-tag-seperator">in</div>
                    <i
                      class="information-parent-tag-icon"
                      :class="informationIcon(option.parentType)"
                    />
                    <div class="information-parent-tag-name">
                      {{option.parentDisplayName}}
                    </div>
                  </div>
                </div>
                <div slot="option" slot-scope="{ option }" class="information-type">
                  <i :class="informationIcon(option.type)" class="information-type-icon" />
                  <div class="information-type-name">{{ option.displayName }}</div>
                  <!-- module -->
                  <fts-label-group
                    :item="getModule(option.connectionId)"
                    emoji
                    :emoji-size="12"
                    class="information-module-tag"
                    v-if="option.connectionId && getModule(option.connectionId)"
                  />
                  <!-- parent -->
                  <div class="information-parent-tag" v-if="option.parentDisplayName">
                    <div class="information-parent-tag-seperator">in</div>
                    <i
                      class="information-parent-tag-icon"
                      :class="informationIcon(option.parentType)"
                    />
                    <div class="information-parent-tag-name">
                      {{option.parentDisplayName}}
                    </div>
                  </div>
                </div>
                </fts-select>

                <div
                  style="margin-top: .275rem;font-size: .75rem;color: var(--fts-gray-5)"
                  v-if="formData.referenceModule && !formData.referenceModuleId"
                >
                  Configuration incomplete, please select Reference Module. Hint: 
                  <span class="fts-underline">{{formData.referenceModule}}</span>
                </div>
              </template>
            </fts-form-element>
            <!-- reference:module table -->
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'referenceModuleTable',
                icon: 'fts-icon-menu-caret-down',
                required: true,
              }"
              :validator="$v.formData"
              comfort-design
              class="fts-col"
              key="referenceModuleTable"
              v-if="formData.referenceModule"
            >
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                :form-data="formData"
                :item="item"
                reduce-key="displayName"
                label="displayName"
                :options="referenceModuleTables"
                comfort-design
                @input="onInput"
                @search:focus="preloadReferenceModuleTable"
                @search:blur="onBlur"
                @optionSelected="updateReferenceModuleTable"
                @clearSelection="updateReferenceModuleTable"
              >
                <div
                  slot="selected-option-container"
                  slot-scope="{ option }"
                  class="information-type"
                >
                  <i :class="informationIcon(option.type)" class="information-type-icon" />
                  <div class="information-type-name">{{ option.displayName }}</div>
                </div>
                <div slot="option" slot-scope="{ option }" class="information-type">
                  <i :class="informationIcon(option.type)" class="information-type-icon" />
                  <div class="information-type-name">{{ option.displayName }}</div>
                </div>
              </fts-select>
            </fts-form-element>
            <!-- reference:display field -->
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'referenceDisplayField',
                icon: 'fts-icon-menu-caret-down',
                required: true,
              }"
              :validator="$v.formData"
              comfort-design
              class="fts-col"
              key="referenceDisplayField"
              v-if="formData.referenceModuleTable"
            >
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                :form-data="formData"
                :item="item"
                reduce-key="displayName"
                label="displayName"
                :options="referenceDisplayFields"
                comfort-design
                @input="onInput"
                @search:blur="onBlur"
                @optionSelected="updateReferenceDisplayField"
                @clearSelection="updateReferenceDisplayField"
              >
                <div
                  slot="selected-option-container"
                  slot-scope="{ option }"
                  class="information-type"
                >
                  <i :class="informationIcon(option.type)" class="information-type-icon" />
                  <div class="information-type-name">{{ option.displayName }}</div>
                </div>
                <div slot="option" slot-scope="{ option }" class="information-type">
                  <i :class="informationIcon(option.type)" class="information-type-icon" />
                  <div class="information-type-name">{{ option.displayName }}</div>
                </div>
              </fts-select>
            </fts-form-element>
          </div>
        </div>
        <!-- select:options -->
        <div
          class="design-modal-form-group design-modal-card design-modal-card-mt"
          v-if="formData.type === 27 && !formData.reference"
        >
          <fts-form-element
            :form-data="formData"
            :item="{
              displayName: 'Options',
              fieldName: 'optionList',
              icon: 'fts-icon-menu-caret-down',
            }"
            comfort-design
            key="optionList"
          >
            <draggable
              slot="formElement"
              slot-scope="{ formData, item }"
              handle=".option-handle"
              draggable=".option-draggable"
              v-model="formData[item.fieldName]"
              v-bind="dragOptions"
              class="information-options"
              @start="draggableOnDrag('informationOptions', true)"
              @end="draggableOnDrag('informationOptions', false)"
              ref="informationOptions"
            >
              <div
                class="option-draggable"
                v-for="(option, optionIndex) in formData.optionList"
                :key="option.id"
              >
                <div class="option-wrapper">
                  <font-awesome-icon
                    :icon="['fas', 'grip-vertical']"
                    class="option-handle"
                  />
                  <div class="option-l">
                    <fts-contenteditable
                      text-wrap
                      v-model="option.name"
                      :style="{ background: getOptionColor(option) }"
                      placeholder="Please enter a name"
                      class="option-name"
                      @input="fieldValidatorTouch('optionList', option.id, 'name')"
                      @keydown:enter="addSelectOption"
                      :ref="`option${optionIndex}`"
                    />
                  </div>
                  <div class="option-action">
                    <swatches
                      class="option-color"
                      v-model="option.color"
                      row-length="5"
                      :swatches="colorSchemes"
                      shapes="circles"
                      popover-x="left"
                      popover-y="top"
                      :swatch-size="25"
                    />
                    <fts-btn
                      icon
                      text
                      @click="removeSelectOption(option)"
                    >
                      <font-awesome-icon :icon="['far', 'trash-alt']" />
                    </fts-btn>
                  </div>
                </div>
                <div
                  class="option-error"
                  v-if="fieldValidatorInvalid('optionList', option.id, 'name')"
                >
                  Option Name is required.
                </div>
              </div>
              <!-- footer -->
              <fts-btn
                slot="footer"
                sm
                outline
                color="primary"
                text-transform-init
                class="option-btn-add"
                @click="addSelectOption"
              >
                <font-awesome-icon :icon="['fas', 'plus']" /> Add Option
              </fts-btn>
            </draggable>
          </fts-form-element>
        </div>
        <!-- fields -->
        <fts-form-element
          :form-data="formData"
          :item="{
            displayName: 'Fields',
            fieldName: 'fields',
            icon: 'fts-icon-list-table',
            required: true,
          }"
          :validator="$v.formData"
          comfort-design
          key="fields"
          v-if="formData.type === 14 || formData.type === 15"
        >
          <draggable
            slot="formElement"
            slot-scope="{ formData, item }"
            handle=".field-handle"
            draggable=".field-wrapper"
            v-model="formData[item.fieldName]"
            v-bind="dragOptions"
            class="information-fields"
            @start="draggableOnDrag('informationFields', true)"
            @end="draggableOnDrag('informationFields', false)"
            ref="informationFields"
          >
            <div
              class="field-wrapper draggable"
              v-for="x in formData.fields"
              :key="x.id"
              @click="openInformationField(x)"
            >
              <font-awesome-icon
                :icon="['fas', 'grip-vertical']"
                class="field-handle stopPropagation"
              />

              <div class="information-type">
                <i
                  :class="informationIcon(x.type)"
                  class="information-type-icon"
                />
                <div class="information-type-name">{{ x.displayName }}</div>
              </div>

              <div class="field-action">
                <fts-btn
                  icon
                  text
                  class="stopPropagation"
                  @click="removeInformationField(x)"
                >
                  <font-awesome-icon :icon="['far', 'trash-alt']" />
                </fts-btn>
              </div>
            </div>
            <!-- footer -->
            <fts-btn
              slot="footer"
              sm
              outline
              color="primary"
              text-transform-init
              class="field-btn-add"
              @click="openInformationField"
            >
              <font-awesome-icon :icon="['fas', 'plus']" /> Add Field
            </fts-btn>
          </draggable>
        </fts-form-element>
        <!-- advanced -->
        <fts-collapsible-element v-model="advanced" v-if="beta">
          <fts-btn
            slot="toggle"
            slot-scope="{ isVisible, toggle }"
            text
            text-transform-init
            :class="{ open: isVisible }"
            class="design-modal-advanced-toggle"
            @click="toggle()"
          >
            <div
              class="design-modal-advanced-toggle-error"
              v-if="advancedSettingsError"
            />
            Advanced settings
            <font-awesome-icon
              :icon="['fas', 'caret-right']"
              class="design-modal-advanced-toggle-icon"
            />
          </fts-btn>
          <div slot="content" class="design-modal-advanced-content fts-row gutters">
            <!-- field name -->
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'fieldName',
                type: 0,
                required: true,
              }"
              :validator="$v.formData"
              comfort-design
              class="fts-col"
            >
              <template #errorMessage>
                {{error("fieldName")}}
              </template>
            </fts-form-element>
            <!-- locus display template -->
            <div
              class="fts-col design-modal-form-group"
              key="displayTemplate"
              v-if="formData.type === 7"
            >
              <div class="design-modal-form-label">Display Template</div>
              <fts-template-editor
                type="text"
                :information="moduleInformation"
                v-model="formData.displayTemplate"
                @input="updateDisplayTemplate"
              />
            </div>
            <!-- locus value template -->
            <div
              class="fts-col design-modal-form-group"
              key="valueTemplate"
              v-if="formData.type === 7 && formData.displayTemplate"
            >
              <div class="design-modal-form-label">Value Template</div>
              <div class="design-modal-form-helper">
                If you wish the value different from the display template, then
                you may custom your own value template. And it is optional.
              </div>
              <fts-template-editor
                type="text"
                :information="moduleInformation"
                v-model="formData.valueTemplate"
              />
            </div>
            <!-- unique key -->
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'uniqueKey',
                input: 'toggleSwitch',
              }"
              comfort-design
              class="fts-col"
              key="uniqueKey"
              v-if="formData.type === 0 && !Boolean(findParent)"
            />
            <!-- can add -->
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'canAddNew',
                input: 'toggleSwitch',
              }"
              comfort-design
              class="fts-col"
              key="canAddNew"
              v-if="formData.type === 7"
            />
            <!-- allow other -->
            <div
              class="fts-col"
              key="allowOther"
              v-if="formData.type === 7 && beta"
            >
              <fts-release-badge />
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'allowOther',
                  displayName: 'Allow Other (Beta)',
                  input: 'toggleSwitch',
                }"
                comfort-design
              />
            </div>
            <!-- assign -->
            <div
              class="fts-col"
              key="assign"
              v-if="formData.type === 20"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'assign',
                  input: 'toggleSwitch',
                }"
                comfort-design
              />
            </div>
            <!-- dashboard due -->
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'dashboardDue',
                input: 'toggleSwitch',
              }"
              comfort-design
              class="fts-col"
              key="dashboardDue"
              v-if="formData.type === 5 || formData.type === 6"
            />
            <!-- date time format -->
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'predefinedFormatId',
                displayName: 'Display Format',
                icon: 'fts-icon-menu-caret-down',
              }"
              comfort-design
              class="fts-col"
              key="predefinedFormatId"
              v-if="formData.type === 5 || formData.type === 6"
            >
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item }"
                :formData="formData"
                :item="item"
                :options="dateFormats"
                comfort-design
              />
            </fts-form-element>
            <!-- custom format -->
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'customFormat',
                type: 0,
              }"
              comfort-design
              class="fts-col"
              key="customFormat"
              v-if="formData.type === 3"
            />
            <!-- number format -->
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'decimalPlaces',
                type: 2,
              }"
              comfort-design
              class="fts-col"
              key="decimalPlaces"
              v-if="formData.type === 3"
            />
            <!-- max file size -->
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'maxFileSize',
                displayName: 'Max File Size (MB)',
                type: 2,
              }"
              comfort-design
              class="fts-col"
              key="maxFileSize"
              v-if="[10, 11, 12, 13].indexOf(formData.type) > -1"
            />
            <!-- column width -->
            <div
              class="fts-col"
              key="columnWidth"
              v-if="isTableField && beta"
            >
              <fts-release-badge />
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'columnWidth',
                  type: 2,
                }"
                comfort-design
              />
            </div>
          </div>
        </fts-collapsible-element>
        <!-- advanced:error -->
        <div
          class="design-modal-form-error"
          v-if="beta && !advanced && advancedSettingsError"
        >
          Error found in Advanced settings.
        </div>
        <!-- modal -->
        <fts-information-design-modal
          child
          :parent="formData"
          :parent-fields="formData && formData.fields"
          :disable-continue="disableContinue"
          ref="ftsInformationDesignModal"
        />
      </template>
    </template>
    <!-- footer:left -->
    <template #footerLeft>
      <fts-btn
        text
        icon
        @click="remove" 
        v-if="!loading && !isSystem && isExist"
      >
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
    </template>
    <!-- footer:right -->
    <template #footerRight>
      <fts-btn
        text
        @click="reset"
        v-if="viewOnly"
      >
        Close
      </fts-btn>

      <template v-else>
        <fts-btn
          text
          @click="beforeClose"
        >
          Cancel
        </fts-btn>

        <fts-btn
          outline
          color="primary"
          :disabled="loading"
          @click="done"
        >
          <font-awesome-icon :icon="['fas', 'check']" /> Ok
        </fts-btn>

        <fts-btn
          outline
          color="primary"
          @click="doneContinue"
          v-if="continuable"
        >
          <font-awesome-icon :icon="['fas', 'chevron-right']" /> Continue
        </fts-btn>
      </template>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);
import { required } from "vuelidate/lib/validators";

import changesets from "diff-json";
import draggable from "vuedraggable";
import Swatches from "vue-swatches";

import commons from "../commons/commons";
import validator from "../commons/designValidator";

import FTSDesignModal from "./FTSDesignModal";
import FTSBtn from "../elements/FTSBtn";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSFormElement from "../elements/FTSFormElement";
import FTSSelect from "../elements/FTSSelect";
import FTSTemplateEditor from "./FTSTemplateEditor";
import FTSLabelGroup from "../elements/display/FTSLabelGroup";
import FTSSpinner from "../elements/FTSSpinner";
import FTSReleaseBadge from "../elements/FTSReleaseBadge";

// workaround
const informationReservedKeywords =
  validator.informationReservedKeywords.concat(["username"]);
const informationReservedFieldName = 
  validator.informationReservedFieldName;
// end workaround

export default {
  name: "fts-information-design-modal",
  components: {
    draggable,
    Swatches,
    "fts-design-modal": FTSDesignModal,
    "fts-btn": FTSBtn,
    "fts-contenteditable": FTSContenteditable,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-form-element": FTSFormElement,
    "fts-select": FTSSelect,
    "fts-template-editor": FTSTemplateEditor,
    "fts-label-group": FTSLabelGroup,
    "fts-spinner": FTSSpinner,
    "fts-release-badge": FTSReleaseBadge,
  },
  props: {
    child: Boolean,
    parent: Object,
    parentFields: Array,
    disableContinue: Boolean,
  },
  data: () => ({
    serialNumberError: false,
    serialNumberExpand: false,
    serialNumberPayload: {},
    serialNumber: 0,
    formData: null,
    originalData: null,
    resolve: null,
    continueCallBack: null,
    params: null,
    advanced: false,
    moduleOptions: [],
    loading: false,
    autofillLoading: false,
    designNameHasFocus: false,
    /* preference */
    referenceModuleTables: [],
    /* drag options */
    dragOptions: commons.dragOptions,
    /* date formats */
    dateFormats: commons.dateFormats,
    /* color schemes */
    colorSchemes: commons.lightenPalette,
    /* hints */
    hints: [
      { id: 0, hint: "alphabets, numbers, symbols and spaces in single line" },
      { id: 1, hint: "yes or no, true or false" },
      { id: 2, hint: "whole number without decimals" },
      { id: 3, hint: "numbers with decimal points" },
      { id: 4, hint: "currency" },
      { id: 5, hint: "calendar date" },
      { id: 6, hint: "calendar date with time" },
      { id: 7, hint: "entry from another module in the same workspace" },
      {
        id: 8,
        hint: "multiple entries with another module in the same workspace",
      },
      { id: 9, hint: "location with place name on map" },
      {
        id: 10,
        hint: "single picture, photo in the format of jpg, jpeg, png, bmp and gif",
      },
      {
        id: 11,
        hint: "multiple pictures, photos in the format of jpg, jpeg, png, bmp and gif",
      },
      {
        id: 12,
        hint: "single file for example, document, spreadsheet, presentation, and pdf",
      },
      {
        id: 13,
        hint: "multiple files for example, document, spreadsheet, presentation, and pdf",
      },
      {
        id: 14,
        hint: "contains defined fields (columns) and rows. Please defined fields below",
      },
      { id: 15, hint: "beta supported (list)" },
      {
        id: 16,
        hint: "plain text such as words, sentences or paragraph with new lines",
      },
      { id: 17, hint: "beta supported (action)" },
      { id: 18, hint: "beta supported (hidden)" },
      { id: 19, hint: "not supported (username)" },
      { id: 20, hint: "entry from profile (module) in the same workspace" },
      { id: 21, hint: "not supported (users)" },
      { id: 22, hint: "formatted text with styles" },
      { id: 23, hint: "not supported (collaboration)" },
      { id: 24, hint: "beta supported (context)" },
      { id: 25, hint: "email address for example: inistate@email.com" },
      { id: 26, hint: "phone number with country and area code" },
      {
        id: 27,
        hint: "dropdown selection with predefined options, please define options below",
      },
      { id: 28, hint: "beta supported (tag)" },
      { id: 29, hint: "digitally signning" },
      { id: 30, hint: "not supported (date range)" },
      { id: 31, hint: "not supported (date time range)" },
      { id: 32, hint: "not supported (formula)" },
      { id: 32, hint: "web page links, starts with https://" },
      { id: 33, hint: "not supported (state)" },
    ],
  }),
  validations() {
    let formData = {};

    if (this.isSystem) {
      // system validation
    } else {
      // let list = this.$store.getters.canvasInformation;
      let list = this.information;
      if (
        this.findInformation &&
        this.findInformation.parent &&
        Array.isArray(this.findInformation.parent.fields)
      ) {
        list = this.findInformation.parent.fields;
      } else if (this.parentFields && Array.isArray(this.parentFields)) {
        list = this.parentFields;
      } else if (this.child) {
        list = [];
      }
      formData = validator.informationDesign(list);
      /* workaround */
      /* workaround:displayName */
      formData.displayName = {
        ...formData.displayName,
        nonReserved: (val) =>
          val
            ? !informationReservedKeywords.some(
                (x) => x === val.toString().toLowerCase().trim()
              )
            : true,
      };
      /* workaround:fieldName */
      if (this.beta)
        formData.fieldName = {
          ...formData.fieldName,
          nonReserved: (val) => val ?
            informationReservedFieldName.indexOf(val) === -1
            : true,
        }
      /* end workaround */
      if (this.formData.type == 27 && !this.formData.reference) {
        formData.optionList = {
          $each: {
            name: { required },
            $trackBy: "id",
          }
        }
      }
    }

    return { formData };
  },
  methods: {
    async show(item, saveCallBack, continueCallBack, params) {
      this.continueCallBack = continueCallBack;
      this.formData = _.cloneDeep(item);
      this.params = params;
      this.loading = true;

      return new Promise(async (resolve) => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });
            } else {
              this.reset();
            }
          },
        });
        
        /* system:document id */
        if (this.formData && this.formData.id == "documentId") {
          this.formData.documentIdFormat = this.vector.documentIdFormat;
          try {
            const res = await this.$store.dispatch("retrieveSerialNumber");
            this.serialNumber = res.data.serial;
          } catch(err) {
            console.error(err);
            this.serialNumberError = true;
          }
        }
        /* connection type */
        if (this.isConnectionType) {
          try {
            await this.loadModules();
            const selectedModule = this.moduleOptions.find(
              (x) => x.id === this.formData.connectionId
            );
            if (
              selectedModule &&
              selectedModule.name !== this.formData.connection
            ) {
              this.$set(this.formData, "connection", selectedModule.name);
            }
          } catch (err) {
            console.error(err);
          }
        }
        /* reference */
        if (this.formData.type === 27 && this.formData.reference) {
          if (this.formData.referenceModuleId) {
            await this.loadReferenceModuleTable();
          }

          if (this.formData.referenceModuleFieldId) {
            const field = this.referenceInformation.find(
              x => x.id === this.formData.referenceModuleFieldId
            );
            if (field) {
              this.formData.referenceModule = field.displayName;
              this.formData.referenceModuleField = field.fieldName;
            } else {
              console.error("InformationDesign:Reference", "Undefined Reference Module Field");
            }
          }

          if (this.formData.referenceModuleTableId) {
            const field = this.referenceModuleTables.find(
              x => x.id === this.formData.referenceModuleTableId
            );
            if (field) {
              this.formData.referenceModuleTable = field.displayName;
            } else {
              console.error("InformationDesign:Reference", "Undefined Reference Module Table");
            }
          }

          if (this.formData.referenceDisplayFieldId) {
            const field = this.referenceDisplayFields.find(
              x => x.id === this.formData.referenceDisplayFieldId
            );
            if (field) {
              this.formData.referenceDisplayField = field.displayName;
            } else {
              console.error("InformationDesign:Reference", "Undefined Reference Display Field");
            }
          }
        }
        /* information type */
        if (this.formData.type === undefined) {
          await this.getAutoInformationType();
        }

        this.loading = false;
        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
          if (!this.isSystem) {
            this.$refs.designerName.focus();
            this.designNameHasFocus = true;
          }
        });
      });
    },
    done() {
      this.compactData();
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        //set documentid
        this.resolve && this.resolve(this.formData);
        this.reset();
      } else {
        console.warn(
          "InformationDesign:InvalidForm",
          validator.translateValidations(this.$v.formData, true)
        );
      }
    },
    doneContinue() {
      if (!this.continuable) return;

      if (
        this.formData.type === 14 &&
        (!this.formData.fields ||
          (this.formData.fields && this.formData.fields.length === 0)) &&
        this.$v.formData.displayName &&
        !this.$v.formData.displayName.$invalid
      ) {
        this.openInformationField();
        return;
      }

      this.compactData();
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        const formData = _.cloneDeep(this.formData);
        const continueCallBack = Object.assign(this.continueCallBack);
        /* reset */
        this.reset();
        /* execute continueCallBack */
        continueCallBack && continueCallBack(formData);
        this.$toasted.info(`Information: ${formData.displayName} is added.`);
      } else {
        console.warn(
          "InformationDesign:InvalidForm",
          validator.translateValidations(this.$v.formData, true)
        );
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    remove() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete ${this.formData.displayName}?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.resolve && this.resolve(true);
              this.reset();
            },
          },
        ],
      });
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    async saveSerial() {
      try {
        await this.$store.dispatch("saveSerialNumber", this.serialNumberPayload);
        this.serialNumberExpand = false;
        this.serialNumber = this.serialNumberPayload.serialNumber;
        this.$toasted.success(
          "Serial number is successfully set to " +
            this.serialNumberPayload.serialNumber
        );
      } catch (ex) {
        this.$toasted.error(ex.response.data.message);
      }
    },
    getHint(informationType) {
      if (informationType != null) {
        let hint = this.hints.find((x) => x.id == informationType);
        if(hint) {
          return hint['hint'];
        }
      }
      return null;
    },
    async getAutoInformationType() {
      if (this.formData && this.formData.displayName) {
        const fallbackType = () => {
          try {
            return this.informationTypes[0].id
          } catch(err) {
            console.warn('InformationDesign:Type', err);
            return 0;
          } 
        }

        try {
          const res = await this.$store.dispatch("ai", {
            type: "information",
            text: this.formData.displayName,
          });
          const type = this.informationTypes.find((x) => x.id === res.data);
          if (type) {
            this.$set(this.formData, "type", res.data);
          } else {
            this.$set(this.formData, "type", fallbackType());
          }
        } catch(err) {
          console.warn('InformationDesign:Type', err);
          this.$set(this.formData, "type", fallbackType());
        }
      }
    },
    debounceInput: _.debounce(async (vm) => {
      await vm.getAutoInformationType();
      vm.autofillLoading = false;
    }, 800),
    dispatchAutofill() {
      if (!this.isExist) {
        if (!this.autofillLoading) this.autofillLoading = true; 
        this.debounceInput(this);
      }
    },
    error(key) {
      // workaround
      if (
        key === "displayName" &&
        this.$v.formData.displayName &&
        this.$v.formData.displayName.$error &&
        !this.$v.formData.displayName.nonReserved
      ) {
        const reducer = (res, val, ind, arr) =>
          ind
            ? ind === arr.length - 1
              ? `${res}, and ${val}`
              : `${res}, ${val}`
            : val;
        return `Oops! you cannot use reserved words: ${(Array.isArray(
          informationReservedKeywords
        )
          ? informationReservedKeywords
          : []
        ).reduce(reducer, null)}}`;
      }
      // end workaround
      return validator.informationDesignError(this.$v.formData, key);
    },
    fieldValidatorTouch(parentFieldName, fieldId, fieldName) {
      try {
        validator.fieldValidatorTouch(
          this.$v.formData[parentFieldName].$each.$iter,
          fieldId,
          fieldName
        );
      } catch(err) {
        console.error("InformationDesign:FieldValidatorTouch", err);
      }
    },
    fieldValidatorInvalid(parentFieldName, fieldId, fieldName) {
      try {
        return validator.fieldValidatorInvalid(
          this.$v.formData[parentFieldName].$each.$iter,
          fieldId,
          fieldName
        );
      } catch(err) {
        console.error("InformationDesign:fieldValidatorInvalid", err);
      }
    },
    informationIcon(id) {
      return commons.informationIcon(id) || "fts-icon-text";
    },
    getModule(id) {
      if (id && this.$store.getters.workspaceModules) {
        const module = this.$store.getters.workspaceModules.find((x) => x.id == id);
        if (module)
          return {
            id: module.id,
            name: module.name,
            emoji: module.emoji,
          }
      }
    },
    compactData() {
      if (this.formData.type != 0) {
        this.$delete(this.formData, "uniqueKey");
      }
      if (!this.isConnectionType) {
        this.updateConnection();
      }
    },
    /* display template */
    updateDisplayTemplate(val) {
      if (!val && this.formData.valueTemplate)
        delete this.formData.valueTemplate;
    },
    /* fields */
    async openInformationField(selected) {
      if (commons.elementStopPropagation()) return;

      let vm = this;
      let item = { ...selected };

      if (!item.id) {
        item = {
          id: commons.generateFieldName(),
          fieldName: commons.generateFieldName(),
        };
      }

      let items = this.formData.fields;

      function set(result, save) {
        if (!items) {
          vm.$set(vm.formData, "fields", []);
          items = vm.formData.fields;
        }
        const findIndex = items.findIndex((x) => x.id == item.id);
        if (findIndex > -1) {
          vm.$set(items, findIndex, result);
        } else {
          items.push(result);
        }
      }
      const result = await this.$refs.ftsInformationDesignModal.show(
        item,
        (formData) => {
          set(formData, true);
        },
        (continueFormData) => {
          set(continueFormData);
          this.openInformationField();
        }
      );
      if (result != null && result != true) {
        set(result);
      } else if (result == true) {
        const findIndex = items.findIndex((x) => x.id == item.id);
        if (findIndex > -1) items.splice(findIndex, 1);
      }
    },
    removeInformationField(item) {
      this.formData.fields.splice(this.formData.fields.indexOf(item), 1);
    },
    /* select */
    toggleSelectSwitch(val) {
      this.$set(this.formData, "reference", val);
    },
    addSelectOption() {
      if (!this.formData.optionList) this.$set(this.formData, "optionList", []);

      const option = {
        id: commons.newUUID(),
        name: null,
      };
      this.formData.optionList.push(option);

      this.$nextTick(() => {
        this.$refs[
          `option${this.formData.optionList.indexOf(option)}`
        ][0].$el.focus();
      });
    },
    removeSelectOption(item) {
      this.formData.optionList.splice(
        this.formData.optionList.indexOf(item),
        1
      );
    },
    getOptionColor(item) {
      return commons.selectOptionBackground({
        option: item,
        optionList: this.formData.optionList,
      });
    },
    /* connection */
    async loadModules() {
      try {
        const res = await this.$store.dispatch("loadVectorSelection");
        const moduleOptions = res.data ? res.data : [];
        if (this.formData.type === 20)
          this.moduleOptions = moduleOptions.filter((x) => x.isUserCollection);
        else 
          this.moduleOptions = moduleOptions;
      } catch(err) {
        this.moduleOptions = [];
        console.error("InformationDesign:LoadModules", err);
      }
    },
    async preloadModules(loading) {
      loading(true);
      await this.loadModules();
      loading(false);
    },
    updateConnection(item) {
      // connection name
      if (item && item.name) 
        this.$set(this.formData, "connection", item.name);
      else if (this.formData.connection)
        this.$delete(this.formData, "connection");
      // connection id
      if (!item && this.formData.connectionId)
        this.$delete(this.formData, "connectionId");
      // reset connection field
      this.updateConnectionField();
    },
    updateConnectionField(item) {
      // connection field Id
      if (item && item.id)
        this.$set(this.formData, "connectionFieldId", item.id);
      else 
        this.$delete(this.formData, "connectionFieldId");
      // connection field name
      if (item && item.fieldName)
        this.$set(this.formData, "connectionFieldName", item.fieldName);
      else if (this.formData.connectionFieldName)
        this.$delete(this.formData, "connectionFieldName");
      // connection field
      if (!item && this.formData.connectionField)
        this.$delete(this.formData, "connectionField");
    },
    /* reference */
    async loadReferenceModuleTable() {
      try {
        const res = await this.$store.dispatch("loadModuleInformation", this.formData.referenceModuleId);
        this.referenceModuleTables = res.filter(x => x.type == 14);
      } catch(err) {
        this.referenceModuleTables = [];
        console.error("InformationDesign:PreloadReferenceModuleTable", err);
      }
    },
    async preloadReferenceModuleTable(loading) {
      loading(true); 
      await this.loadReferenceModuleTable();
      loading(false);
    },
    async updateReferenceModule(item) {
      /* reset reference module table */
      this.updateReferenceModuleTable();
      /* update */
      if (item && item.displayName) {
        this.$set(this.formData, "referenceModule", item.displayName);
        this.$set(this.formData, "referenceModuleId", item.connectionId);
        this.$set(this.formData, "referenceModuleField", item.fieldName);
        await this.loadReferenceModuleTable();
        if (this.referenceModuleTables.length) {
          this.$set(this.formData, "referenceModuleTable", this.referenceModuleTables[0].displayName);
          this.updateReferenceModuleTable(this.referenceModuleTables[0]);
        }
      } else {
        this.$delete(this.formData, "referenceModule");
        this.$delete(this.formData, "referenceModuleId");
        this.$delete(this.formData, "referenceModuleField");
      }
    },
    updateReferenceModuleTable(item) {
      /* reset reference display field */
      this.updateReferenceDisplayField();
      /* update */
      if (item && item.id) {
        this.$set(this.formData, "referenceModuleTableId", item.id);
        if (this.referenceDisplayFields.length) {
          this.$set(this.formData, "referenceDisplayField", this.referenceDisplayFields[0].displayName);
          this.updateReferenceDisplayField(this.referenceDisplayFields[0]);
        }
      } else {
        this.$delete(this.formData, "referenceModuleTable");
        this.$delete(this.formData, "referenceModuleTableId");
      }
    },
    updateReferenceDisplayField(item) {
      if (item && item.id) {
        this.$set(this.formData, "referenceDisplayFieldId", item.id);
      } else {
        this.$delete(this.formData, "referenceDisplayField");
        this.$delete(this.formData, "referenceDisplayFieldId");
      }
    },
    /* drag and drop */
    draggableOnDrag(reference, condition) {
      if (condition)
        this.$refs[reference] &&
          this.$refs[reference].$el &&
          this.$refs[reference].$el.classList.add("drag");
      else
        this.$refs[reference] &&
          this.$refs[reference].$el &&
          this.$refs[reference].$el.classList.remove("drag");
    },
  },
  computed: {
    vector() {
      return this.$store.state.vector;
    },
    beta() {
      return this.$store.state.beta;
    },
    workspaceModules() {
      return this.$store.getters.workspaceModules;
    },
    information() {
      return this.params && this.params.information ? this.params.information : this.$store.getters.canvasInformation;
    },
    referenceInformation() {
      const information = this.information.filter(x => x.type == 7 || x.type == 20);
      if (this.findParent && Array.isArray(this.findParent.fields)) {
        const fields = this.findParent.fields.filter(
          x => x.type == 7 || x.type == 20
        ).map(
          y => ({ 
            ...y,
            parentDisplayName: this.findParent.displayName,
            parentType: this.findParent.type,
          })
        );
        return information.concat(fields);
      }
      return information;
    },
    referenceDisplayFields() {
      try {
        const field = this.referenceModuleTables.find(
          x => x.id === (this.formData && this.formData.referenceModuleTableId)
        );
        if (field && field.fields) return field.fields;
      } catch(err) {
        console.error("InformationDesign:ReferenceDisplayFields", err);
      }
      return [];
    },
    findInformation() {
      return _.findDeep(
        this.$store.getters.canvasInformation,
        (x) => x.id == this.formData.id,
        {
          childrenPath: "fields",
        }
      );
    },
    findParent() {
      if (this.parent) return this.parent;
      else if (
        this.findInformation &&
        this.findInformation.parent &&
        this.findInformation.parent.id
      )
        return this.findInformation.parent;
    },
    isExist() {
      return Boolean(this.findInformation);
    },
    informationTypes() {
      if (this.params && this.params.informationTypes) {
        return this.params.informationTypes;
      }
      return commons.informationTypes.filter(
        (x) =>
          x.input !== "dateRange" &&
          x.input !== "formula" &&
          x.input !== "multidropDown" &&
          x.input !== "list" &&
          x.input !== "username" &&
          !(this.isTableField && x.input === "table")
      );
    },
    isSystem() {
      return this.formData && this.formData.system;
    },
    isConnectionType() {
      return (
        this.formData && [7, 8, 20, 21, 24].indexOf(this.formData.type) > -1
      );
    },
    isTableField() {
      return (
        ((this.findInformation &&
          this.findInformation.parent &&
          this.findInformation.parent.type) ||
          (this.parent && this.parent.type)) === 14
      );
    },
    continuable() {
      return !this.isSystem && !this.isExist && !this.disableContinue;
    },
    moduleInformation() {
      if (this.formData && this.formData.connectionId) {
        const module = this.moduleOptions.find(
          (x) => x.id == this.formData.connectionId
        );
        return module && Array.isArray(module.information) ? module.information : [];
      }
    },
    headerHintHelper() {
      return (
        this.continuable &&
        this.designNameHasFocus &&
        !this.autofillLoading &&
        !this.isExist &&
        this.formData &&
        this.formData.displayName &&
        this.$v.formData &&
        !this.$v.formData.$error &&
        this.$v.formData.type &&
        !this.$v.formData.type.$invalid
      );
    },
    advancedSettingsError() {
      const fields = ["fieldName"];
      return fields.some(x => {
        try {
          return this.$v.formData[x].$error;
        } catch(err) {
          console.error("InformationDesign:AdvancedSettingsError", err);
        }
      });
    },
    viewOnly() {
      const fields = ["createdBy", "date", "lastChangeBy", "lastChangeDate"];
      return this.isSystem && fields.indexOf(this.formData && this.formData.id) > -1;
    },
  },
  watch: {
    "formData.type": function(val, oldVal) {
      if (
        val != null && val != undefined &&
        oldVal != null && oldVal !== undefined
      ) {
        /* reset connection */
        if (this.isConnectionType) this.updateConnection();
      }
    }
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-information-design-modal {
  /* parent */
  .information-parent {
    display: flex;
    align-items: center;
    margin-left: 0.75rem;
    font-size: 0.875rem;
    .information-parent-seperator {
      margin-right: 0.575rem;
      color: $grayLightColor;
    }
    .information-parent-icon {
      margin-right: 0.375rem;
      color: $secondaryColor;
    }
    .information-parent-name {
      @include text-overflow-ellipsis;
      max-width: 100px;
    }
  }
  /* hint-helper */
  .information-type-hint-helper {
    margin-top: -1rem;
    font-size: 0.975rem;
    color: $grayLightColor;
  }
  /* options */
  .information-options {
    &.drag {
      .option-action {
        visibility: hidden;
      }
    }
    .option-wrapper {
      position: relative;
      min-width: 0;
      display: flex;
      align-items: center;
      margin-left: -0.75rem;
      padding: 0.275rem 0 0.275rem 0.75rem;
      &:hover {
        .option-handle {
          display: block;
        }
      }
      &.sortable-drag {
        .option-action {
          visibility: hidden;
        }
      }
      .option-handle {
        @include absolute-center-y;
        left: 0;
        display: none;
        font-size: 0.75rem;
        color: $grayLightColor;
        cursor: grab;
      }
      .option-l {
        flex: 1;
        display: flex;
        align-items: center;
        .option-name {
          width: auto;
          font-size: 1rem;
          padding: 0.375rem 0.575rem;
          border-radius: 0.375rem;
        }
      }
      .option-action {
        display: flex;
        align-items: center;
        margin-left: 0.75rem;
      }
      .option-color {
        margin-right: 0.75rem;
        @include flex-center-middle;
        &.vue-swatches {
          .vue-swatches__trigger {
            height: 20px !important;
            width: 20px !important;
            min-width: 20px;
          }
          .vue-swatches__container {
            position: absolute;
            right: 0px;
          }
        }
      }
    }
    .option-error {
      margin-top: -0.175rem;
      margin-bottom: .175rem;
      font-size: .75rem;
      color: var(--fts-danger);
    }
    .option-btn-add {
      margin-top: 0.375rem;
    }
  }
  /* fields */
  .information-fields {
    &.drag {
      .field-action {
        visibility: hidden;
      }
    }
    .field-wrapper {
      position: relative;
      min-width: 0;
      display: flex;
      align-items: center;
      margin-left: -0.75rem;
      padding: 0.275rem 0 0.275rem 0.75rem;
      &:hover {
        cursor: pointer;
        .field-handle {
          display: block;
        }
      }
      &.sortable-drag {
        .field-action {
          visibility: hidden;
        }
      }
      .field-handle {
        @include absolute-center-y;
        left: 0;
        display: none;
        font-size: 0.75rem;
        color: $grayLightColor;
        cursor: grab;
      }
      .information-type {
        flex: 1;
      }
      .field-action {
        margin-left: 0.75rem;
      }
    }
    .field-btn-add {
      margin: 0.375rem 0;
    }
  }
  /* serial number */
  .serial-number {
    .design-modal-label-group .design-modal-label-group-r {
      width: 75px;
      display: flex;
      justify-content: end;
    }
    .serial-number-action {
      margin-top: -1rem;
    }
  }
  /* select */
  .information-select-switch {
    @include flex-gap($gap: .375rem, $display: flex);
    margin-bottom: .75rem;
    .select-switch {
      &.select-switch--active {
        background: rgba($color: $primaryColor, $alpha: 0.095);
        color: $primaryColor;
      }
      @include theme {
        background: rgba(theme-get('fts-form-label'), 0.095);
      }
      display: inline-flex;
      padding: .575rem 1rem;
      border-radius: .25rem;
      color: var(--fts-form-label);
      font-size: .875rem;
      font-weight: 500;
      cursor: pointer;
    }
  }
}
/* information tag */
@mixin information-tag($flexShrink: null, $maxWidth: null) {
  @include theme() {
    background-color: rgba(theme-get('fts-gray-5'), .15);
  }
  @if $flexShrink {
    flex-shrink: $flexShrink;
  }
  @if $maxWidth {
    max-width: $maxWidth;
  }
  padding: 0.15rem .2rem;
  border-radius: .25rem;
  font-size: 0.75rem;
  color: var(--fts-gray-5);
  line-height: 1;
  &:not(:first-child) {
    margin-left: .375rem;
  }
}
/* information type */
.information-type {
  min-width: 0;
  display: flex;
  align-items: center;
  font-size: 1rem;
  &.system {
    .information-type-icon {
      color: $primaryColor;
    }
  }
  &:not(.system) {
    .information-type-icon {
      color: $secondaryColor;
    }
  }
  .information-type-icon {
    margin-left: 0.175rem;
    margin-right: 0.575rem;
  }
  .information-type-name {
    @include text-overflow-ellipsis;
  }
  .information-hint {
    color: var(--fts-gray-5);
    font-size: .75rem;
  }
  /* information */
  .information-module-tag {
    @include information-tag($flexShrink: 0, $maxWidth: 110px);
  }
  /* information parent tag */
  .information-parent-tag {
    @include information-tag($flexShrink: 0, $maxWidth: 110px);
    min-width: 0;
    display: flex;
    align-items: center;
    .information-parent-tag-seperator {
      margin-right: 0.375rem;
    }
    .information-parent-tag-icon {
      margin-right: 0.375rem;
      color: $secondaryColor;
    }
    .information-parent-tag-name {
      @include text-overflow-ellipsis;
    }
  }
}
/* module option emoji */
.design-modal-module-option-emoji {
  margin-right: 0.575rem !important;
}
</style>